import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Seo } from "@/components/Seo";
import { cn } from "@/lib/utils";

/**
 * Renders a collapsible FAQ list and injects FAQPage JSON-LD via <Seo />.
 */
export function FaqSection({ heading = "Questions we get asked", items, className }) {
  const [openIdx, setOpenIdx] = useState(0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: typeof it.a === "string" ? it.a : it.a?.props?.children?.toString?.() || "" },
    })),
  };

  return (
    <section
      data-testid="faq-section"
      className={cn("bg-white", className)}
    >
      <Seo jsonLd={jsonLd} />
      <div className="mx-auto max-w-4xl px-4 py-16 lg:px-6 lg:py-24">
        <h2 className="font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl">
          {heading}
        </h2>
        <div className="mt-8 divide-y divide-wafa-deep/10 border-y border-wafa-deep/10">
          {items.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  className="flex w-full items-start justify-between gap-4 py-5 text-start"
                >
                  <span className="font-heading text-base font-semibold text-wafa-deep sm:text-lg">
                    {it.q}
                  </span>
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-wafa-deep/20 text-wafa-deep">
                    {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </span>
                </button>
                {open ? (
                  <div className="pb-6 pe-10 text-sm leading-relaxed text-wafa-ink/80 lg:text-base">
                    {it.a}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
